// @ts-strict-ignore
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { CodingArticleUtils } from '@article/util/coding-article.utils';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleCreationManager } from '@project/components/article/manager/article-creation.manager';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Injectable, OnDestroy } from '@angular/core';
import { ArticleCreationManagerFactory } from '../manager/article-creation.manager.factory';
import { Log } from '@shared/debug/log';
import { ArticleCreationService } from '../service/article-creation.service';
import { Subscription } from 'rxjs';
import { CodingArticleProcessorDelegator } from './coding-article-processor.delegator';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';

@Injectable({ providedIn: 'root' })
export class CodingArticleProcessor implements OnDestroy {
  private _articleCreationManager: ArticleCreationManager;

  public enableCodingFingerAttachedHandler: boolean = false;
  private _addedArticlesSubscription: Subscription;

  constructor(
    _codingArticleProcessorDelegator: CodingArticleProcessorDelegator,
    private articleCreationManagerFactory: ArticleCreationManagerFactory,
    private articleManager: ArticleManager,
    private _articleCreationService: ArticleCreationService
  ) {
    _codingArticleProcessorDelegator.codingArticleProcessor = this;
    this.init();
  }

  private init(): void {
    this._articleCreationManager = this.articleCreationManagerFactory.getNewInstance();
    this.subscribeOnAddedArticles();
  }

  public get articleCreationManager () {
    return this._articleCreationManager;
  }

  public ngOnDestroy(): void {
    this._addedArticlesSubscription?.unsubscribe();
    this._addedArticlesSubscription = undefined;
  }

  private subscribeOnAddedArticles(): void {
    this._addedArticlesSubscription = this._articleCreationService.articlesAdded$.subscribe({
      next: (data) => {
        if (data.type !== 'articlesAdded') {
          return;
        }
        if (data.success) {
          this.codingFingerAttachedHandler();
        }
      },
      error: (error) => Log.error('Error in added articles subscription: ', error)
    })
  }

  private codingFingerAttachedHandler(): void {
    if (this.enableCodingFingerAttachedHandler) {
      this.enableCodingFingerAttachedHandler = false;
      this._articleCreationManager.clean();
    }
  }

  public findCodingFingerUsageIdByParent(parentArticleUsage: ArticleUsageVO, position: number = 1): string {
    if (!parentArticleUsage) {
      return null;
    }

    const parentArticle: ArticleVO = this.articleManager.getCachedArticle(parentArticleUsage.articleId, parentArticleUsage.articleVersion);

    if (!CodingArticleUtils.hasMultipleCodingFingerConnections(parentArticle)) {
      position = 1;
    }
    const connectionName: string = CodingArticleUtils.findCodingFingerConnectionName(parentArticle, position);
    if (!connectionName) {
      return null;
    }

    let codingFingerArticleUsageId: string = null;
    for (const connectionAreaUsage of ArticleUsageUtils.connectionAreaUsagesByName(parentArticleUsage, connectionName)) {
      for (const givingAreaId of connectionAreaUsage.connection.givingAreaIds) {
        codingFingerArticleUsageId = givingAreaId.articleUsageId;
      }
    }
    return codingFingerArticleUsageId;
  }

  public clean(): void {
    this._articleCreationManager.clean();
  }

  public getCreatedArticleContainers(): ArticleContainer[] {
    return this._articleCreationManager.getCreatedArticleContainers();
  }
}
